import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { BlackBaseTheme } from "./constants/theme";
// components
import { PreStateLayout } from "./Components/PreState";
import { PostStateLayout } from "./Components/PostState";
import BlackCloseIcon from "./Components/BlackCloseIcon";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
// types
import { M2CSwiggyBlackHalfCardProps, States } from "./types";
import { Content } from "@swiggy-private/cart-types";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { recordNREvent } from "@swiggy-private/checkout-core";
import { fetchLottie, useEffectOnce } from "@swiggy-private/core-ui";
// constants
import {
    M2C_HALFCARD_TEST_IDS,
    CLOSED_CTA_TEXT,
    DEFAULT_PROGRESS_DURATION,
    POST_STATE_CLOSE_CTA_TEXT,
    THOUSAND,
    DELAY_FOR_PROGRESS_BAR,
    CLOSED_CTA_TEXT_GRATIFICATION,
    M2C_BLACK_RENEWAL_COIN_LOTTIE,
} from "./constants";
// styles
import { PostStateHalfcard, PreStateHalfcard } from "./styles";
// context
import { M2CHalfcardContext } from "@super-ui/contexts";

const M2CSwiggyBlackHalfCard: FC<M2CSwiggyBlackHalfCardProps> = ({
    preStateData,
    postStateData,
    state = States.PRE_STATE,
    onClose,
}) => {
    const [stateToRender, setStateToRender] = useState(state);
    const [addingSuperToCart, setAddingSuperToCart] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [lottieData, setLottieData] = useState<null>(null);
    const { duration = DEFAULT_PROGRESS_DURATION, setIsM2CGratificationShown } = postStateData || {};
    const {
        analyticsEvents: { onClickM2CHalfcardSkipCta },
    } = useContext(M2CHalfcardContext);

    const assetCtx = useMemo(() => preStateData?.bottomSheetData.assetCtx, [preStateData?.bottomSheetData.assetCtx]);

    useEffectOnce(() => {
        fetchLottie({
            lottieId: assetCtx?.[0]?.link || "",
            setLottieData,
            setLottieFailed: () => recordNREvent({ eventType: M2C_BLACK_RENEWAL_COIN_LOTTIE }),
        });
    });

    // Used ref here to get the height of the preState to set the height of the postState to make the experience consistent
    const preStateHeightRef = useRef<HTMLDivElement>(null);
    const postStateHeightRef = useRef<number>(0);

    const isPreState = useMemo(() => stateToRender === States.PRE_STATE && !isEmpty(preStateData), [
        preStateData,
        stateToRender,
    ]);
    const isPostState = useMemo(() => stateToRender === States.POST_STATE || isEmpty(preStateData), [
        preStateData,
        stateToRender,
    ]);

    const onCloseAction = useCallback(
        (ctaText: string, title?: string, content?: Content[]) => {
            setIsOpen(false);
            onClickM2CHalfcardSkipCta(ctaText, title, JSON.stringify(content));
            if (isPreState) {
                preStateData?.closeBottomSheet?.(ctaText);
            } else if (isPostState) {
                setIsM2CGratificationShown?.(true);
                postStateData?.closeBottomSheet?.(ctaText);
            }
            typeof onClose === "function" && onClose();
        },
        [
            onClickM2CHalfcardSkipCta,
            isPreState,
            isPostState,
            onClose,
            preStateData,
            setIsM2CGratificationShown,
            postStateData,
        ],
    );

    useEffect(() => {
        if (isPreState && addingSuperToCart && !isEmpty(postStateData)) {
            setStateToRender(States.POST_STATE);
            setAddingSuperToCart(false);
            typeof onClose === "function" && onClose();
        }
    }, [duration, postStateData, stateToRender, addingSuperToCart, isPreState, isPostState, onCloseAction, onClose]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        const totalDuration = duration + DELAY_FOR_PROGRESS_BAR;
        if (isPostState) {
            timeout = setTimeout(() => {
                onCloseAction(POST_STATE_CLOSE_CTA_TEXT);
            }, totalDuration * THOUSAND);
        }

        return () => clearTimeout(timeout);
    }, [duration, postStateData, stateToRender, addingSuperToCart, isPreState, isPostState, onCloseAction]);

    useEffect(() => {
        postStateHeightRef.current = Number(preStateHeightRef?.current?.offsetHeight);
    }, []);

    const preStateProps = useMemo(() => {
        if (!isEmpty(preStateData)) {
            return {
                ...preStateData,
                addingSuperToCart,
                setAddingSuperToCart,
                closeBottomSheet: onCloseAction,
                setStateToRender,
                preStateHeightRef,
                onClose,
            };
        }
    }, [addingSuperToCart, onCloseAction, preStateData, onClose]);

    if (!isEmpty(assetCtx) && isEmpty(lottieData)) {
        return null;
    }

    return (
        <ThemeProvider theme={BlackBaseTheme} colorMode="dark">
            <HalfCard
                isOpen={isOpen}
                closeOnOverlayClick
                hasCustomPadding
                onClose={() => onCloseAction(CLOSED_CTA_TEXT)}
                overflowY="visible"
                hideCloseIcon
            >
                <BlackCloseIcon
                    onClick={() =>
                        onCloseAction(
                            !isEmpty(preStateProps) ? CLOSED_CTA_TEXT : CLOSED_CTA_TEXT_GRATIFICATION,
                            postStateData?.title,
                            postStateData?.content,
                        )
                    }
                />
                {!isEmpty(preStateProps) && isPreState && (
                    <PreStateHalfcard data-testid={M2C_HALFCARD_TEST_IDS.HALFCARD} addingSuperToCart={isPostState}>
                        <PreStateLayout {...preStateProps} />
                    </PreStateHalfcard>
                )}

                {!isEmpty(postStateData) && isPostState && (
                    <PostStateHalfcard
                        data-testid={M2C_HALFCARD_TEST_IDS.HALFCARD}
                        height={Number(postStateHeightRef?.current)}
                    >
                        <PostStateLayout {...postStateData} />
                    </PostStateHalfcard>
                )}
            </HalfCard>
        </ThemeProvider>
    );
};

export default memo(M2CSwiggyBlackHalfCard);
